import { el } from '../ui/format';
import type { HintLevel } from '../game/rescue';
import type { Action, InputSource, RawInput } from './input';

/** A phone's gas and brake: two buttons (the default), or one slider (the pause poster switches; gp.pedals). */
export type Pedals = 'buttons' | 'slider';

/**
 * Full steering lock this far (px) from where the thumb landed: about 12 mm on an upright phone, 18 mm on one on
 * its side (a wider screen, and the thumb has room to travel).
 */
const REACH_UPRIGHT = 72;
const REACH_SIDEWAYS = 112;
/** Never less than this either way, for a thumb that lands right by the screen's edge. */
const MIN_REACH = 24;
/**
 * The pedal slider, sliding down from where the thumb landed (px): the gas eases off over GAS_SPAN, nothing for
 * COAST_GAP, then the brake builds over BRAKE_SPAN, so a thumb can trail off the brake into a corner and squeeze
 * the gas back on: about 28 mm from full gas to full brake. All shorter (down to 0.6) for a thumb that lands low.
 */
const GAS_SPAN = 70;
const COAST_GAP = 12;
const BRAKE_SPAN = 90;
const MIN_SCALE = 0.6;
const SLIDE = GAS_SPAN + COAST_GAP + BRAKE_SPAN;
/** A pedal button takes a touch this far past its drawn circle (the buttons' area is padded by as much: main.css). */
const PEDAL_SLACK = 22;

type Pedal = 'gas' | 'brake';

/**
 * Phone controls: the left thumb steers, the right thumb works the gas and brake.
 *
 * Steering: put the left thumb down anywhere in the lower left and slide. The steering starts where the thumb
 * landed (a slider drawn under it), with full lock a reach either way, or at the screen's edge for a thumb that
 * lands nearer to it.
 *
 * Pedals, two ways. Buttons: GAS and BRAKE, side by side, and on an upright phone BRAKE straight above GAS (where
 * it was, the steering thumb at full right lock sat on it); one padded area takes both, a touch goes to the
 * nearer one, and a thumb can slide from one to the other. Slider: put the right thumb down anywhere in the lower
 * right for full gas; slide down and the gas eases off, then the brake builds (both by how far); lift to coast.
 *
 * Pause and restart sit beside the lap timer (under it, upright), where no thumb goes while driving. The
 * driving lesson points at the buttons, so they're up while it runs. Multi-touch via pointer events.
 */
export class TouchControls implements InputSource {
  readonly root: HTMLElement;
  active = false;
  private chosen: Pedals = 'buttons';
  private lessonOn = false;
  // The steering.
  private pad: HTMLElement;
  private rail: HTMLElement;
  private knob: HTMLElement;
  private steerId: number | null = null;
  private steerOrigin = 0;
  /** Full lock this far right of where the thumb landed, and this far left (less by the screen's edge). */
  private reach = REACH_UPRIGHT;
  private reachLeft = REACH_UPRIGHT;
  private steer = 0;
  // The pedal buttons.
  private pedals: HTMLElement;
  private gasBtn: HTMLElement;
  private brakeBtn: HTMLElement;
  private pressing = new Map<number, Pedal>();
  // The pedal slider.
  private slide: HTMLElement;
  private slideKnob: HTMLElement;
  private slideBand: HTMLElement;
  private slideGuide: HTMLElement;
  private slideMark: HTMLElement;
  private demoEl: HTMLElement;
  private slideId: number | null = null;
  private slideY = 0;
  /** How much shorter the slide is for this landing (1: full length). */
  private slideScale = 1;
  private slideGas = 0;
  private slideBrake = 0;

  constructor(parent: HTMLElement, onAction: (a: Action) => void, onUse: () => void) {
    // The layer itself lets taps through (to the HUD's buttons); only the controls on it catch them.
    this.root = el('div', 'touch hidden', parent);

    this.pad = el('div', 'touch-steer interactive', this.root);
    this.rail = el('div', 'touch-rail', this.root);
    for (let i = 0; i < 3; i++) el('i', '', this.rail);
    this.knob = el('div', 'touch-knob', this.rail);

    this.pedals = el('div', 'touch-pedals interactive', this.root);
    const brake = el('button', 'touch-brake', this.pedals, 'BRAKE');
    el('span', 'touch-call', brake, 'hold to back out');
    this.brakeBtn = brake;
    this.gasBtn = el('button', 'touch-gas', this.pedals, 'GAS');

    this.slide = el('div', 'touch-slide interactive', this.root);
    // Where to put the thumb, and how the slide goes: a ring, the scale under it.
    const rest = el('div', 'touch-slide-rest', this.root);
    el('i', 'ring', rest);
    this.scale(rest);
    el('div', 'touch-slide-call', this.root, 'pull down and hold to back out');
    el('div', 'touch-restart-call', this.root, 'tap ↺ for a fresh lap');
    // While the thumb's down: the scale beside where it landed, the thumb's place on it, the knob and its words.
    this.slideGuide = el('div', 'touch-slide-guide', this.root);
    this.slideMark = this.scale(this.slideGuide);
    this.slideKnob = el('div', 'touch-slide-knob', this.root);
    this.slideBand = el('div', 'touch-slide-band', this.root);
    this.demoEl = el('div', 'touch-demo', this.root);
    el('div', 'thumb', this.demoEl);
    const words = el('div', 'words', this.demoEl);
    for (const w of ['thumb down for gas', 'slide down to ease off', 'further down to brake', 'lift to coast']) el('span', '', words, w);

    const restart = el('button', 'touch-restart interactive', this.root, '↺');
    // Two bars drawn in CSS (the pause character turns into an emoji on iPhones).
    const pause = el('button', 'touch-pause interactive', this.root);
    pause.setAttribute('aria-label', 'Pause');

    // --- the steering
    this.pad.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (this.steerId !== null) return;
      this.steerId = e.pointerId;
      capture(this.pad, e.pointerId);
      const p = this.local(e);
      this.steerOrigin = p.x;
      const box = this.root.getBoundingClientRect();
      this.reach = box.width > box.height ? REACH_SIDEWAYS : REACH_UPRIGHT;
      this.reachLeft = Math.max(MIN_REACH, Math.min(this.reach, p.x - 4));
      // The slider, under the thumb.
      Object.assign(this.rail.style, { left: `${p.x - this.reachLeft}px`, top: `${p.y}px`, bottom: 'auto', width: `${this.reachLeft + this.reach}px` });
      this.rail.style.setProperty('--o', `${this.reachLeft}px`);
      this.rail.classList.add('on');
      this.moveSteer(p.x);
      onUse();
    });
    this.pad.addEventListener('pointermove', (e) => {
      if (e.pointerId === this.steerId) this.moveSteer(this.local(e).x);
    });
    const endSteer = (e: PointerEvent): void => {
      if (e.pointerId === this.steerId) this.releaseSteer();
    };
    this.pad.addEventListener('pointerup', endSteer);
    this.pad.addEventListener('pointercancel', endSteer);

    // --- the pedal buttons (the nearer one; a thumb can slide across)
    this.pedals.addEventListener('pointerdown', (e) => {
      const which = this.pedalAt(e.clientX, e.clientY);
      if (!which) return;
      e.preventDefault();
      capture(this.pedals, e.pointerId);
      this.pressing.set(e.pointerId, which);
      this.showPedals();
      onUse();
      onAction('start');
    });
    this.pedals.addEventListener('pointermove', (e) => {
      const now = this.pressing.get(e.pointerId);
      if (!now) return;
      const which = this.pedalAt(e.clientX, e.clientY);
      if (which && which !== now) {
        this.pressing.set(e.pointerId, which);
        this.showPedals();
      }
    });
    const lift = (e: PointerEvent): void => {
      if (this.pressing.delete(e.pointerId)) this.showPedals();
    };
    this.pedals.addEventListener('pointerup', lift);
    this.pedals.addEventListener('pointercancel', lift);

    // --- the pedal slider
    this.slide.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (this.slideId !== null) return;
      this.slideId = e.pointerId;
      capture(this.slide, e.pointerId);
      const p = this.local(e);
      const box = this.root.getBoundingClientRect();
      this.slideY = p.y;
      this.slideScale = Math.max(MIN_SCALE, Math.min(1, (box.height - 8 - p.y) / SLIDE));
      const k = this.slideScale;
      Object.assign(this.slideGuide.style, { left: `${p.x}px`, top: `${p.y}px`, height: `${SLIDE * k}px` });
      this.slideGuide.style.setProperty('--gas', `${GAS_SPAN * k}px`);
      this.slideGuide.style.setProperty('--brake', `${(GAS_SPAN + COAST_GAP) * k}px`);
      this.slideKnob.style.left = this.slideBand.style.left = `${p.x}px`;
      this.showDemo(false);
      this.root.classList.add('slide-on');
      this.moveSlide(p.y);
      onUse();
      onAction('start');
    });
    this.slide.addEventListener('pointermove', (e) => {
      if (e.pointerId === this.slideId) this.moveSlide(this.local(e).y);
    });
    const endSlide = (e: PointerEvent): void => {
      if (e.pointerId === this.slideId) this.releaseSlide();
    };
    this.slide.addEventListener('pointerup', endSlide);
    this.slide.addEventListener('pointercancel', endSlide);

    restart.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      onAction('restart');
    });
    // Pause on the lift, not the press: the poster then comes up under a finger that's already off
    // the glass, so the tap can't land on its "resume".
    pause.addEventListener('pointerdown', (e) => e.preventDefault());
    pause.addEventListener('pointerup', (e) => {
      e.preventDefault();
      onAction('pause');
    });
    // No click after the tap: the poster is up by then, and the click would land on whatever's under the finger.
    pause.addEventListener('touchend', (e) => e.preventDefault(), { passive: false });
  }

  /** The pedal slider is what works the gas and brake right now (chosen, and no lesson running). */
  get sliding(): boolean {
    return this.chosen === 'slider' && !this.lessonOn;
  }

  get pedalKind(): Pedals {
    return this.chosen;
  }

  setPedals(p: Pedals): void {
    this.chosen = p;
    this.apply();
  }

  /** The driving lesson is running: it points at the pedal buttons. */
  setLesson(on: boolean): void {
    this.lessonOn = on;
    this.apply();
  }

  /** The slider's how-to (a faint thumb going through the moves), until the thumb goes down. */
  showDemo(on: boolean): void {
    this.demoEl.classList.toggle('on', on && this.sliding);
  }

  /**
   * The car is stuck against something: point at how to back out (hold BRAKE, or the slider pulled down). Words
   * and a pulse the first time, a soft glow after (game/rescue.ts); null takes it down.
   */
  backOut(level: HintLevel | null): void {
    this.root.classList.toggle('backout', level !== null);
    this.root.classList.toggle('backout-words', level === 'words');
  }

  /** A crash that ruins the lap: point at restart, by the clock. */
  restartHint(level: HintLevel | null): void {
    this.root.classList.toggle('restart-hint', level !== null);
    this.root.classList.toggle('restart-words', level === 'words');
  }

  private apply(): void {
    this.releaseSteer();
    this.releaseSlide();
    this.pressing.clear();
    this.showPedals();
    this.root.classList.toggle('slide', this.sliding);
    if (!this.sliding) this.showDemo(false);
  }

  /** The slider's scale: gas fading out, a gap (coasting), the brake building; and a mark for the thumb. */
  private scale(parent: HTMLElement): HTMLElement {
    const s = el('div', 'scale', parent);
    el('i', 'gas', s);
    el('i', 'brake', s);
    return el('b', '', s);
  }

  private local(e: PointerEvent): { x: number; y: number } {
    const r = this.root.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private moveSteer(x: number): void {
    const dx = x - this.steerOrigin;
    const v = dx < 0 ? Math.max(-1, dx / this.reachLeft) : Math.min(1, dx / this.reach);
    this.steer = -v; // drag right = steer right = negative
    this.knob.style.setProperty('--k', `${v * (v < 0 ? this.reachLeft : this.reach)}px`);
  }

  private releaseSteer(): void {
    this.steerId = null;
    this.steer = 0;
    // Back to its resting place (main.css).
    for (const k of ['left', 'top', 'bottom', 'width', '--o']) this.rail.style.removeProperty(k);
    this.knob.style.removeProperty('--k');
    this.rail.classList.remove('on');
  }

  private moveSlide(y: number): void {
    const k = this.slideScale;
    const dy = Math.max(0, Math.min(SLIDE * k, y - this.slideY));
    this.slideGas = Math.max(0, 1 - dy / (GAS_SPAN * k));
    this.slideBrake = Math.max(0, Math.min(1, (dy - (GAS_SPAN + COAST_GAP) * k) / (BRAKE_SPAN * k)));
    const band = this.slideBrake > 0 ? 'brake' : this.slideGas > 0 ? 'gas' : 'coast';
    this.slideKnob.style.top = this.slideBand.style.top = `${this.slideY + dy}px`;
    this.slideKnob.className = `touch-slide-knob ${band}`;
    // The ring shows how much: full gas or full brake brightest.
    this.slideKnob.style.setProperty('--level', String(band === 'brake' ? this.slideBrake : band === 'gas' ? this.slideGas : 1));
    this.slideMark.style.top = `${dy}px`;
    const pct = (f: number): string => `${Math.round(f * 100)}%`;
    this.slideBand.textContent = band === 'brake' ? `brake ${pct(this.slideBrake)}` : band === 'coast' ? 'coast' : this.slideGas < 1 ? `gas ${pct(this.slideGas)}` : '';
  }

  private releaseSlide(): void {
    this.slideId = null;
    this.slideGas = 0;
    this.slideBrake = 0;
    this.slideBand.textContent = '';
    this.root.classList.remove('slide-on');
  }

  /** The pedal a touch at (x, y) means: the nearer one, if it's within reach of its edge. */
  private pedalAt(x: number, y: number): Pedal | null {
    let best: Pedal | null = null;
    let gap = Infinity;
    for (const [which, b] of [['gas', this.gasBtn], ['brake', this.brakeBtn]] as const) {
      const r = b.getBoundingClientRect();
      const d = Math.hypot(x - (r.left + r.width / 2), y - (r.top + r.height / 2)) - r.width / 2;
      if (d < gap) {
        gap = d;
        best = which;
      }
    }
    return gap <= PEDAL_SLACK ? best : null;
  }

  private showPedals(): void {
    const held = [...this.pressing.values()];
    this.gasBtn.classList.toggle('down', held.includes('gas'));
    this.brakeBtn.classList.toggle('down', held.includes('brake'));
  }

  /**
   * Points at a pedal (the driving lesson's "brake now"), or at neither: a
   * ring pulses out from it (styles/lesson.css). Never a transform, which
   * the press feedback owns.
   */
  cue(which: Pedal | null): void {
    this.gasBtn.classList.toggle('cue', which === 'gas');
    this.brakeBtn.classList.toggle('cue', which === 'brake');
  }

  show(v: boolean): void {
    this.active = v;
    this.root.classList.toggle('hidden', !v);
    document.body.classList.toggle('touch', v);
  }

  sample(out: RawInput): void {
    if (!this.active) return;
    out.steer += this.steer;
    if (this.sliding) {
      out.throttle = Math.max(out.throttle, this.slideGas);
      out.brake = Math.max(out.brake, this.slideBrake);
      return;
    }
    const held = [...this.pressing.values()];
    if (held.includes('gas')) out.throttle = Math.max(out.throttle, 1);
    if (held.includes('brake')) out.brake = Math.max(out.brake, 1);
  }
}

function capture(el: HTMLElement, id: number): void {
  try {
    el.setPointerCapture(id);
  } catch {
    /* not an active pointer (synthetic events); the control still works */
  }
}

export function isTouchDevice(): boolean {
  return typeof window !== 'undefined' && (window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window);
}
